import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { useDemo } from "../context/DemoContext";
import { getProduct } from "../data/content";
import { trackEvent, EVENTS } from "../services/analytics";

// Shared product-page template. All copy comes from src/data/content.js.
export default function ProductPage() {
  const { productId } = useParams();
  const product = getProduct(productId);
  const { openDemo } = useDemo();

  useEffect(() => {
    if (product) trackEvent(EVENTS.PRODUCT_PAGE_VIEW, { product: product.id });
  }, [product]);

  if (!product) return <Navigate to="/404" replace />;

  return (
    <>
      <SEO title={product.seo.title} description={product.seo.description} path={product.path} />

      <section className="pt-28 pb-14 sm:pt-36 sm:pb-16" data-testid={`product-hero-${product.id}`}>
        <div className="container-x grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {product.logo && (
              <Reveal>
                <img
                  src={product.logo}
                  alt={`${product.name} logo`}
                  className="mb-6 h-12 w-auto object-contain"
                  data-testid="product-logo"
                />
              </Reveal>
            )}
            <span className="eyebrow">{product.category}</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl">
              <MaskedLines lines={[product.name, product.headline]} />
            </h1>
            <Reveal delay={0.3}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
                {product.description}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={product.url}
                  className="btn-primary"
                  onClick={() => trackEvent(EVENTS.PRODUCT_CTA_CLICK, { product: product.id })}
                  data-testid="product-explore-cta"
                >
                  Explore {product.name} <ArrowUpRight size={16} aria-hidden="true" />
                </a>
                <button
                  type="button"
                  onClick={() => openDemo(product.id)}
                  className="btn-secondary"
                  data-testid="product-demo-cta"
                >
                  Book a Demo
                </button>
              </div>
              {product.note && (
                <p className="mt-5 max-w-xl text-xs leading-relaxed text-slate-500">{product.note}</p>
              )}
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="clip-frame overflow-hidden rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5">
              <img
                src={product.screenshots ? product.screenshots[0].src : product.image}
                alt={product.screenshots ? product.screenshots[0].alt : product.imageAlt}
                className={`h-72 w-full sm:h-96 ${
                  product.screenshots?.[0].fit === "contain"
                    ? "bg-slate-50 object-contain py-4"
                    : "object-cover object-left-top"
                }`}
                loading="eager"
                data-testid="product-hero-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-testid="product-capabilities">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow">Capabilities</span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              What {product.name} does
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {product.capabilityGroups.map((g, i) => (
              <Reveal key={g.title} delay={i * 0.08} className="h-full">
                <div className="card-line h-full p-6">
                  <h3 className="font-display text-base font-bold text-slate-900">{g.title}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {g.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600">
                        <Check size={15} className="mt-0.5 shrink-0 text-teal-600" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          {product.complianceNote && (
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-3xl rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-xs leading-relaxed text-amber-800" data-testid="product-compliance-note">
                {product.complianceNote}
              </p>
            </Reveal>
          )}
        </div>
      </section>

      {product.screenshots && (
        <section className="py-16 sm:py-20" data-testid="product-screenshots">
          <div className="container-x">
            <Reveal>
              <span className="eyebrow">Inside the product</span>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Real {product.name} screens
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
                Genuine product views with sensitive business and personal details
                masked.
              </p>
            </Reveal>
            <div className="mt-10 grid gap-5 sm:grid-cols-2">
              {product.screenshots.map((s, i) => (
                <Reveal key={s.src} delay={Math.min(i * 0.06, 0.25)} className={i === 0 ? "sm:col-span-2" : ""}>
                  <figure className="card-line group overflow-hidden p-3">
                    <div className="overflow-hidden rounded-xl border border-slate-100">
                      <img
                        src={s.src}
                        alt={s.alt}
                        loading="lazy"
                        className={
                          s.fit === "contain"
                            ? "max-h-[540px] w-full bg-slate-50 object-contain py-4"
                            : "w-full object-cover object-left-top transition-transform duration-500 group-hover:scale-[1.015]"
                        }
                        data-testid={`product-screenshot-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      />
                    </div>
                    <figcaption className="px-2 pt-3 pb-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                      {s.label}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-20" data-testid="product-lifecycle">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow">How it flows</span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              The {product.name} lifecycle
            </h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap items-stretch gap-3">
            {product.lifecycle.map((step, i) => (
              <Reveal key={step} delay={i * 0.07}>
                <div className="flex items-center gap-3">
                  <div className="card-line flex items-center gap-3 px-5 py-4">
                    <span className="font-mono text-xs font-medium text-blue-700">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-sm font-bold text-slate-900">{step}</span>
                  </div>
                  {i < product.lifecycle.length - 1 && (
                    <ArrowRight size={16} className="text-slate-300" aria-hidden="true" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {product.scenarios && (
        <section className="bg-white py-16 sm:py-20" data-testid="product-scenarios">
          <div className="container-x">
            <Reveal>
              <span className="eyebrow">In practice</span>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Example scenarios
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {product.scenarios.map((s, i) => (
                <Reveal key={s.name} delay={i * 0.08} className="h-full">
                  <div className="card-line h-full border-t-2 border-t-teal-600 p-6">
                    <h3 className="font-display text-base font-bold text-slate-900">{s.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.detail}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={`py-16 sm:py-20 ${product.scenarios ? "" : "bg-white"}`} data-testid="product-audiences">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow">Who it's for</span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Built for
            </h2>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            {product.audiences.map((a) => (
              <span
                key={a}
                className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700"
              >
                {a}
              </span>
            ))}
          </div>
          {product.demoFieldsNote && (
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-500">
                {product.demoFieldsNote}
              </p>
            </Reveal>
          )}
          <Reveal delay={0.15}>
            <p className="mt-8 text-sm text-slate-600">
              Looking at the other products?{" "}
              <Link to="/products" className="font-semibold text-blue-700 hover:text-blue-800" data-testid="product-back-to-products">
                See the full portfolio
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection
        eyebrow={`${product.name} enquiries`}
        title={`See ${product.name} on your own operations.`}
        body="Book a demo and we'll walk through the product against your workflows — or send a general enquiry and the team will respond."
        productId={product.id}
      />
    </>
  );
}
